import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Autocomplete from "react-google-autocomplete";
import ImageResize from 'quill-image-resize-module-react';
import * as yup from 'yup';
import {
    useAddOtherContactsMutation,
    useGetEmployerQuery,
    usePostAJobMutation,
    useSendEmail3Mutation,
    useUpdateJobMutation
} from "../store/apiSlice";
import { setEmployer } from '../store/postsSlice';
import { currencies } from '../utils/data';
import InputBlock2 from "./InputBlock2";
import SelectBlock2 from "./SelectBlock2";
import UniSearchBlock from "./UniSearchBlock1";

// --- SETUP & CONFIGURATION (Constants) ---

window.Quill = Quill;
Quill.register('modules/imageResize', ImageResize);

const quillModules = {
    toolbar: [
        [{ header: '1' }, { header: '2' }, { font: [] }],
        [{ size: [] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        ['link', 'image', 'video'],
        ['clean']
    ],
    clipboard: { matchVisual: false },
    imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize']
    }
};

const validationSchema = yup.object({
    title: yup.string().required('Job Title is required.'),
    employer_id: yup.number().required('An employer must be selected.'),
    description: yup.string().required('Job Description cannot be empty.'),
    location: yup.string().required('Location is required.'),
    job_type: yup.string().required('Employment Type is required.'),
    activation_date: yup.date().required('Post Date is required.'),
    expiration_date: yup.date().required('Expiration Date is required.').min(
        yup.ref('activation_date'),
        "Expiration date can't be before the post date."
    ),
    email1: yup.string().email('Must be a valid email.').optional(),
    email2: yup.string().email('Must be a valid email.').optional(),
}).required();

// --- CUSTOM HOOK FOR FORM LOGIC ---

const useJobForm = (job) => {
    const isAddMode = !job;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.auth.userInfo);
    const employer = useSelector((state) => state.posts.employer);

    const [formStatus, setFormStatus] = useState(isAddMode ? "Post Job" : "Update Job");
    const [startTime] = useState(Date.now());

    const [postAJob, { isLoading: isPosting }] = usePostAJobMutation();
    const [updateJob, { isLoading: isUpdating }] = useUpdateJobMutation();
    const [sendEmail] = useSendEmail3Mutation();
    const [addOtherContacts] = useAddOtherContactsMutation();

    const { data: employerData } = useGetEmployerQuery(
        employer?.employer_id, { skip: !employer?.employer_id }
    );

    const defaultValues = useMemo(() => {
        if (isAddMode) {
            return {
                salary_period: 'yearly', currency: 'AUD',
                activation_date: new Date().toISOString().substring(0, 10),
                expiration_date: new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10),
                remote: 'Onsite', job_type: 'Full time',
                featured: false, internalonly: false, headlineOnly: false,
            };
        }
        return {
            ...job,
            activation_date: job.activation_date ? new Date(job.activation_date).toISOString().split('T')[0] : '',
            expiration_date: job.expiration_date ? new Date(job.expiration_date).toISOString().split('T')[0] : '',
        };
    }, [job, isAddMode]);

    const formMethods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues,
    });

    const { reset, setValue, getValues } = formMethods;

    useEffect(() => {
        reset(defaultValues);
        if (job) {
            dispatch(setEmployer({ company_name: job.employer?.company_name, employer_id: job.employer_id, logo: job.employer?.logo, employerPageURL: job.employer.employerPageURL, clientType: job.clientType }));
        }
    }, [job, defaultValues, reset, dispatch]);

    useEffect(() => {
        if (isAddMode && employer?.employer_id) {
            setValue("company_name1", employer.company_name);
            setValue("employer_id", employer.employer_id);
            if (employer.location) setValue("location", employer.location);
            if (employerData?.clientType) {
                setValue("featured", employerData.clientType === "unlimited");
                setValue("headlineOnly", employerData.clientType === "HeadlineOnly");
            }
        }
    }, [isAddMode, employer, employerData, setValue, getValues]);

    // const handleContactLogic = async (jobId, jobTitle, employer_id, contactData, initialContactData = {}) => {
    //     const { email, firstName, lastName, phone, jobTitle: contactJobTitle } = contactData;
    //     const hasChanged = JSON.stringify(contactData) !== JSON.stringify(initialContactData);

    //     if (email && (isAddMode || hasChanged)) {
    //         await sendEmail({ email, firstName, jobId, jobTitle });
    //         await addOtherContacts({
    //             email, phone, firstName, lastName, employer_id,
    //             title: contactJobTitle, contactType: "OTHER"
    //         });
    //     }
    // };

    const onSubmit = async (data) => {
        const jobPostDuration = Math.ceil((Date.now() - startTime) / 1000);

        if (isAddMode) {
            setFormStatus("Posting...");
            try {
                const result = await postAJob({ ...data, jobPostDuration, postedBy: userInfo.id }).unwrap();
                setFormStatus("Job Posted!");
                // await handleContactLogic(result.id, data.title, data.employer_id, { email: data.email1, firstName: data.firstName1, lastName: data.lastName1, phone: data.phone1, jobTitle: data.jobTitle1 });
                // await handleContactLogic(result.id, data.title, data.employer_id, { email: data.email2, firstName: data.firstName2, lastName: data.lastName2, phone: data.phone2, jobTitle: data.jobTitle2 });
                navigate(`/jobs/edit/${result.id}`);
            } catch (error) {
                console.error("Failed to post job:", error); setFormStatus("Post Job");
            }
        } else {
            setFormStatus("Updating...");
            try {
                await updateJob({ ...data, id: job.id, updateTime: jobPostDuration, updatedBy: userInfo.id }).unwrap();
                setFormStatus("Job Updated!");
                // await handleContactLogic(job.id, data.title, data.employer_id,
                //     { email: data.email1, firstName: data.firstName1, lastName: data.lastName1, phone: data.phone1, jobTitle: data.jobTitle1 },
                //     { email: job.email1, firstName: job.firstName1, lastName: job.lastName1, phone: job.phone1, jobTitle: job.jobTitle1 }
                // );
                // await handleContactLogic(job.id, data.title, data.employer_id,
                //     { email: data.email2, firstName: data.firstName2, lastName: data.lastName2, phone: data.phone2, jobTitle: data.jobTitle2 },
                //     { email: job.email2, firstName: job.firstName2, lastName: job.lastName2, phone: job.phone2, jobTitle: job.jobTitle2 }
                // );
            } catch (error) {
                console.error("Failed to update job:", error); setFormStatus("Update Job");
            }
        }
    };

    return { formMethods, onSubmit, formStatus, isAddMode, isLoading: isPosting || isUpdating };
};

// --- UI COMPONENTS ---

const FormSection = ({ title, subtitle, children, className = '' }) => (
    <div className={`bg-white p-6 rounded-lg shadow-md border border-gray-200 ${className}`}>
        <h2 className="text-xl font-bold text-gray-800 mb-1">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mb-6">{subtitle}</p>}
        <div className="grid grid-cols-1 gap-6">
            {children}
        </div>
    </div>
);

const JobDetailsSection = ({ register, control, errors }) => (
    <FormSection title="Job Details">
        <div>
            <label className="label-text font-bold text-md mb-2 block">Job Title</label>
            <InputBlock2 type="text" field="title" register={register} errors={errors} />
        </div>
        <div className="flex flex-col sm:flex-row gap-6 font-bold text-sm">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" {...register('featured')} className="checkbox checkbox-sm" /><span>Featured</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" {...register('internalonly')} className="checkbox checkbox-sm" /><span>Internal Only</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" {...register('headlineOnly')} className="checkbox checkbox-sm" /><span>Headline Only</span></label>
        </div>
        <div>
            <label className="label-text font-bold text-md mb-2 block">Job Description</label>
            <Controller name="description" control={control} render={({ field }) => (<ReactQuill theme="snow" modules={quillModules} value={field.value || ''} onChange={field.onChange} className="bg-white" />)} />
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
        </div>

    </FormSection>
);

const LocationSection = ({ register, setValue, errors, initialLocation }) => {
    const [locationInput, setLocationInput] = useState(initialLocation || '');
    useEffect(() => { setLocationInput(initialLocation || ''); }, [initialLocation]);

    const handlePlaceSelected = (place) => {
        if (!place.geometry) return;
        const { lat, lng } = place.geometry.location;
        setValue("location", place.formatted_address, { shouldValidate: true });
        setValue("latitude", lat()); setValue("longitude", lng());
        setLocationInput(place.formatted_address);
    };

    return (
        <FormSection title="Location Details" subtitle="Start typing and select a location from the suggestions.">
            <Autocomplete className="input input-bordered w-full" apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY} onPlaceSelected={handlePlaceSelected} onChange={(e) => setLocationInput(e.target.value)} value={locationInput} options={{ types: ["geocode", "establishment"], componentRestrictions: { country: "au" }, }} />
            {errors.location && <p className="text-red-500 text-sm">{errors.location.message}</p>}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mt-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("remote")} value="Onsite" className="radio radio-sm" /><span className="label-text font-bold text-sm">Onsite</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("remote")} value="Remote" className="radio radio-sm" /><span className="label-text font-bold text-sm">Remote</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" {...register("remote")} value="Hybrid" className="radio radio-sm" /><span className="label-text font-bold text-sm">Hybrid</span></label>
            </div>
        </FormSection>
    );
};

const ContactsSection = ({ register, errors }) => (
    <FormSection title="Contacts for this Job" subtitle="Optional: Add contacts to notify about this job posting.">
        <div className="p-4 border rounded-md">
            <h3 className="font-bold mb-4">Contact 1</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputBlock2 type="text" field="firstName1" label="First Name" register={register} errors={errors} /> <InputBlock2 type="text" field="lastName1" label="Last Name" register={register} errors={errors} />
                <InputBlock2 type="email" field="email1" label="Email" register={register} errors={errors} /> <InputBlock2 type="text" field="phone1" label="Phone" register={register} errors={errors} />
                <InputBlock2 type="text" field="jobTitle1" label="Job Title" register={register} errors={errors} forceClass="md:col-span-2" />
            </div>
        </div>
        <div className="p-4 border rounded-md">
            <h3 className="font-bold mb-4">Contact 2</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputBlock2 type="text" field="firstName2" label="First Name" register={register} errors={errors} /> <InputBlock2 type="text" field="lastName2" label="Last Name" register={register} errors={errors} />
                <InputBlock2 type="email" field="email2" label="Email" register={register} errors={errors} /> <InputBlock2 type="text" field="phone2" label="Phone" register={register} errors={errors} />
                <InputBlock2 type="text" field="jobTitle2" label="Job Title" register={register} errors={errors} forceClass="md:col-span-2" />
            </div>
        </div>
    </FormSection>
);

const AdminAndActionsSection = ({ job, formMethods }) => {
    const { handleSubmit, onSubmit, setValue } = formMethods;
    const handleExpire = () => { if (window.confirm(`Are you sure you want to expire the job: "${job?.title}"?`)) { setValue('expiration_date', '1900-01-01'); handleSubmit(onSubmit)(); } };
    if (!job) return null;
    return (
        <FormSection title="Admin Actions">
            {job.user && <p className="text-sm text-gray-600">Posted By: <span className="font-semibold">{job.user.firstName} {job.user.lastName}</span></p>}
            <div className="flex flex-wrap gap-4 items-center">
                <Link to={`/jobs/${job.employer.employerPageURL}/${job.id}`} className="btn btn-outline btn-accent" target="_blank" rel="noopener noreferrer">View Live Job</Link>
                <button type="button" onClick={handleExpire} className="btn btn-outline btn-error">Expire This Job</button>
            </div>
        </FormSection>
    );
};

// --- MAIN COMPONENT ---

const JobAddEdit = ({ job }) => {
    const { formMethods, onSubmit, formStatus, isLoading, isAddMode } = useJobForm(job);
    const { register, handleSubmit, formState: { errors }, control, setValue, watch } = formMethods;

    const dispatch = useDispatch();
    const employer = useSelector((state) => state.posts.employer);

    const handleEmployerSelect = (company_name, employer_id, Region, country, logo, employerPageURL, clientType) => {
        setValue("company_name1", company_name, { shouldValidate: true });
        setValue("employer_id", employer_id || 0, { shouldValidate: true });
        dispatch(setEmployer({ company_name, employer_id, logo, employerPageURL, clientType }));
    };

    return (
        <div className=" mx-auto p-4 sm:p-6 lg:p-8 bg-gray-50">

            <form onSubmit={handleSubmit(onSubmit)} noValidate>

                <header className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{isAddMode ? 'Post a New Job' : 'Edit Job'}</h1>
                        <p className="text-gray-600 mt-1">Fill in the details below to {isAddMode ? 'create' : 'update'} the job listing.</p>
                    </div>
                    <div className="sticky top-0">
                        <button type="submit" className="btn btn-primary" disabled={isLoading}>
                            {isLoading && <span className="loading loading-spinner"></span>}
                            {formStatus}
                        </button>
                        {isAddMode && !isLoading && <button type="button" onClick={() => formMethods.reset()} className="btn btn-ghost ml-4">Reset Form</button>}
                    </div>
                </header>

                {/* --- Main Two-Column Layout Grid --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                    {/* --- Left Column --- */}
                    <div className="flex flex-col gap-8">
                        <FormSection title="Institution / Employer">
                            {employer.employerPageURL && <a href={employer.employerPageURL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm mb-4 inline-block">View Institution Recruitment Page &rarr;</a>}
                            <UniSearchBlock
                                register={register}
                                field="employer_name"
                                customKey="employer_name"
                                label="employer_name"
                                value1={employer.company_name}
                                forceClass="mb-2"
                                onChange={handleEmployerSelect}
                            />                            {errors.company_name1 && <p className="text-red-500 text-sm mt-1">{errors.company_name1.message}</p>}
                            {job?.sourceEmployerName && <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded-md mt-2">Original employer name: <span className="font-bold">{job.sourceEmployerName}</span>. Please ensure the correct institution is selected.</p>}
                        </FormSection>

                        <JobDetailsSection register={register} control={control} errors={errors} />

                        {/* <ContactsSection register={register} errors={errors} /> */}
                    </div>

                    {/* --- Right Column --- */}
                    <div className="flex flex-col gap-8">
                        <LocationSection register={register} setValue={setValue} errors={errors} initialLocation={watch('location')} />

                        <FormSection title="Employment & Salary">
                            <SelectBlock2 list={["Full time", "Part time", "Contractor", "Sessional"]} field="job_type" label="Employment Type" register={register} errors={errors} />
                            <div>
                                <label className="label-text font-bold text-md mb-2 block">Salary Range</label>
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                    <SelectBlock2 list={currencies} field="currency" label="Currency" register={register} errors={errors} />
                                    <InputBlock2 type="number" field="salary_from" label="$ From" register={register} errors={errors} />
                                    <InputBlock2 type="number" field="salary_to" label="$ To" register={register} errors={errors} />
                                    <SelectBlock2 list={["yearly", "monthly", "weekly", "daily", "hourly"]} field="salary_period" label="Frequency" register={register} errors={errors} />
                                </div>
                            </div>
                        </FormSection>

                        <FormSection title="Application Details & Dates">
                            <InputBlock2 type="text" field="how_to_apply" label="How to Apply (e.g., external URL)" register={register} errors={errors} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <InputBlock2 type="date" field="activation_date" label="Post Date" register={register} errors={errors} />
                                <InputBlock2 type="date" field="expiration_date" label="Expiration Date" register={register} errors={errors} />
                            </div>
                        </FormSection>

                        {!isAddMode && <AdminAndActionsSection job={job} formMethods={{ ...formMethods, onSubmit }} />}
                    </div>
                </div>


            </form>
        </div>
    );
};

export default JobAddEdit;